import cx from 'classnames';
import { t } from 'i18next';
import { FC } from 'react';
import { recruitmentSearchPaths } from '../../../../../shared/recruitmentShared';
import { RecruitmentEntityType } from '../../../../../../../shared/types/recruitmentTypes';
import { namedRoutes, resolveRoutePath } from '../../../bundles/routes';
import { relativeDateShort } from '../../../../../../../shared/frontend/libs/utils';
import { RecruitmentActivityIcon } from '../../../shared/recruitment/components/RecruitmentActivityIcon';
import RecruitmentProfilePreview from '../../../shared/recruitment/components/RecruitmentProfilePreview/RecruitmentProfilePreview';
import { Link } from '../../../../../../../shared/frontend/components/link';
import { SpecIcon } from '../../../../../../../shared/frontend/scripts/wow/components/SpecIcon';
import { Spinner } from '../../../../../../../shared/frontend/components/presentational/spinner';
import PatronLevel from '../../../../../../../shared/frontend/components/patreon/patronLevel';
import { ComplexTooltip, SimpleTooltip_NEXT } from '../../../../../../../shared/frontend/components/presentational/tooltip';
import { RecruitmentCharacterTitle, RecruitmentGuildTitle, RecruitmentTeamTitle } from './recruitmentTitles';
import { TRecruitmentPublishProfilesProps } from './shared';
import { getProfileTarget } from '../../../../../../../shared/frontend/scripts/recruitment/utils';

const RecruitmentRecentProfiles: FC<TRecruitmentPublishProfilesProps> = (props) => {
  const { profiles, loading } = props;

  if (loading) {
    return (
      <div style={{ position: 'relative' }}>
        <Spinner />
      </div>
    );
  }

  return (
    <table
      className={cx(
        'slds-table',
        'slds-table--bordered',
        'slds-table--striped',
        'slds-table--no-cell-buffer',
        'rio-rankings-table',
        'slds-no-row-hover'
      )}
      style={{ height: '100%' }}
    >
      <thead>
        <tr>
          <th colSpan={5} className="slds-text-align--center text-white slds-text-heading--small">
            {t('frontpage:recruitment.recently-published-profiles')}
            <span className="slds-m-left--x-small">
              <SimpleTooltip_NEXT tooltip={t('frontpage:recruitment.recruitment-tooltip-help')}>
                <i className="fa fa-id-card-alt" />
              </SimpleTooltip_NEXT>
            </span>
          </th>
        </tr>
      </thead>
      <tbody>
        {profiles.length === 0 && (
          <tr>
            <td colSpan={5}>
              <div
                className="slds-p-around--large slds-text-align--center slds-align--absolute-center slds-text-heading--medium"
                style={{ minHeight: '250px', whiteSpace: 'normal' }}
              >
                {t('frontpage:recruitment.no-recruitment-profiles')}
              </div>
            </td>
          </tr>
        )}
        {profiles.map((profile: any, index: number) => {
          const target = getProfileTarget(profile);
          const { region } = target;

          return (
            <tr key={index}>
              <td className="slds-text-align--center text-muted">
                {relativeDateShort(profile.throttled_published_at)}
              </td>
              <td className="slds-text-align--center slds-cell-wrap">
                <Link className="rio-realm-link" to={resolveRoutePath(namedRoutes.RegionHome, { region: region.slug })}>
                  {t(`regions:${region.slug}.short-name`)}
                </Link>
              </td>
              <td className="slds-text-align--center slds-cell-wrap">
                {profile.entity_type === RecruitmentEntityType.Character && (
                  <SimpleTooltip_NEXT
                    placement="top"
                    tooltip={
                      <span className={`class-color--${target.class.slug}`}>
                        {t('common:class-spec', {
                          class: t(`game:classes.${target.class.slug}.name`),
                          spec: t(`game:specs.${target.class.slug}.${target.spec.slug}.name`)
                        })}
                      </span>
                    }
                  >
                    <span className={'slds-avatar slds-avatar--x-small rio-border--light slds-m-left--xx-small'}>
                      <SpecIcon size={18} class={target.class.slug} spec={target.spec.slug} />
                    </span>
                  </SimpleTooltip_NEXT>
                )}
                {profile.entity_type === RecruitmentEntityType.Guild && (
                  <span>{t('frontpage:recruitment.profile-type-guild')}</span>
                )}
                {profile.entity_type === RecruitmentEntityType.Team && (
                  <span>{t('frontpage:recruitment.profile-type-team')}</span>
                )}
              </td>
              <td className="slds-text-align--left text-muted">
                <ComplexTooltip
                  placement="left"
                  tag="span"
                  opacity={1}
                  tooltip={
                    <PatronLevel
                      patronLevel={profile.patronLevel?.level}
                      className="slds-m-horizontal--xx-small"
                      isHidden={profile.entity_type === RecruitmentEntityType.Guild}
                    >
                      <RecruitmentProfilePreview
                        className={cx('rio-shadow--medium', 'character-recruitment-preview--profile-border')}
                        profile={profile}
                        profileType={profile.entity_type}
                        target={{ details: target }}
                        useProfileTimezone={true}
                        showPatronLabel
                      />
                    </PatronLevel>
                  }
                >
                  <div className="slds-grid" style={{ whiteSpace: 'normal' }}>
                    {profile.entity_type === RecruitmentEntityType.Character && (
                      <RecruitmentCharacterTitle title character={target} profile={profile} />
                    )}
                    {profile.entity_type === RecruitmentEntityType.Guild && (
                      <RecruitmentGuildTitle guild={target} profile={profile} />
                    )}
                    {profile.entity_type === RecruitmentEntityType.Team && (
                      <RecruitmentTeamTitle team={target} profile={profile} />
                    )}
                  </div>
                </ComplexTooltip>
              </td>
              <td>
                <div className="slds-grid slds-grid--vertical-align-center">
                  <div className="slds-col slds-grow-none">
                    <span className="slds-avatar slds-avatar--x-small slds-m-right--x-small">
                      <RecruitmentActivityIcon size="xx-small" type={profile.activity_type} />
                    </span>
                  </div>
                  <div className="slds-col slds-grow">
                    {t(`recruitment:entity-activity-title-only.${profile.entity_type}.${profile.activity_type}`)}
                  </div>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={5}>
            <div className="slds-grid slds-grid--vertical-align-center slds-p-horizontal--medium">
              <div className="slds-size--4-of-12 slds-p-around--xx-small slds-text-align--center slds-text-body--small">
                <Link to={recruitmentSearchPaths.guilds} className="rio-realm-link--muted">
                  {t('frontpage:recruitment.find-a-guild')}
                </Link>
              </div>
              <div className="slds-size--4-of-12 slds-p-around--xx-small slds-text-align--center slds-text-body--small">
                <Link to={recruitmentSearchPaths.players} className="rio-realm-link--muted">
                  {t('frontpage:recruitment.find-players')}
                </Link>
              </div>
              <div className="slds-size--4-of-12 slds-p-around--xx-small slds-text-align--center slds-text-body--small">
                <Link to={recruitmentSearchPaths.teams} className="rio-realm-link--muted">
                  {t('frontpage:recruitment.find-a-mplus-team')}
                </Link>
              </div>
            </div>
          </td>
        </tr>
      </tfoot>
    </table>
  );
};

export default RecruitmentRecentProfiles;
